import { getFontById } from "@/domain/options";
import type {
  BackgroundOption,
  IconOption,
  LockedStickerArtifactReference,
  LockedStickerProductArtifact,
  MixSheetRecipe,
  MixSheetRecipeItem,
  SizeId,
  StickerConfiguration,
  StickerSheetOrder,
  TextColorOption,
} from "@/domain/types";
import type { AppLocale } from "@/i18n/routing";
import type { ShopifyLineEntry } from "./sticker-line-contract";
import { SHOPIFY_STICKER_PRODUCT_KEY } from "./sticker-line-contract";

export type CustomerFacingBackgroundResolver = (id: string) => Promise<BackgroundOption | null>;
export type CustomerFacingIconResolver = (id: string) => Promise<IconOption | null>;
export type CustomerFacingTextColorResolver = (id: string) => Promise<TextColorOption | null>;

export const SHOPIFY_STICKER_FULFILLMENT_METAFIELD_NAMESPACE = "custom";
export const SHOPIFY_STICKER_FULFILLMENT_METAFIELD_KEY = "sticker_fullfillment_payload";
export const SHOPIFY_STICKER_FULFILLMENT_METAFIELD_TYPE = "json";
export const SHOPIFY_STICKER_FULFILLMENT_METAFIELD_DEFINITION_NAME = "Sticker fulfillment payload";
export const SHOPIFY_STICKER_FULFILLMENT_METAFIELD_VERSION = "2026-04-hidden-checkout-v1";

export interface ShopifyCustomerFacingAttribute {
  key: string;
  value: string;
}

export interface ShopifyHiddenStickerLineSnapshot {
  cartLineId: string;
  productKey: typeof SHOPIFY_STICKER_PRODUCT_KEY;
  variantId: string;
  configuration: StickerConfiguration;
  lockedArtifact: LockedStickerProductArtifact;
  sheetOrder: StickerSheetOrder;
  customerFacingAttributes: ShopifyCustomerFacingAttribute[];
}

export interface ShopifyHiddenStickerFulfillmentPayload {
  version: typeof SHOPIFY_STICKER_FULFILLMENT_METAFIELD_VERSION;
  lines: ShopifyHiddenStickerLineSnapshot[];
}

const CUSTOMER_FACING_ATTRIBUTE_LABELS: Record<
  AppLocale,
  {
    name: string;
    size: string;
    quantity: string;
    design: string;
  }
> = {
  sv: {
    name: "Namn",
    size: "Storlek",
    quantity: "Antal",
    design: "Design",
  },
  en: {
    name: "Name",
    size: "Size",
    quantity: "Quantity",
    design: "Design",
  },
};

function formatQuantitySummary(sheetOrder: StickerSheetOrder, locale: AppLocale): string {
  const totalStickers = sheetOrder.sheetCount * sheetOrder.stickersPerSheet;

  if (locale === "sv") {
    const sheetLabel = sheetOrder.sheetCount === 1 ? "ark" : "ark";
    const stickerLabel = totalStickers === 1 ? "klistermarke" : "klistermarken";
    return `${sheetOrder.sheetCount} ${sheetLabel} (${totalStickers} ${stickerLabel})`;
  }

  const sheetLabel = sheetOrder.sheetCount === 1 ? "sheet" : "sheets";
  const stickerLabel = totalStickers === 1 ? "sticker" : "stickers";
  return `${sheetOrder.sheetCount} ${sheetLabel} (${totalStickers} ${stickerLabel})`;
}

function inferCustomerFacingAttributeLocale(
  attributes: readonly ShopifyCustomerFacingAttribute[]
): AppLocale {
  return attributes.some(
    (attribute) => attribute.key === CUSTOMER_FACING_ATTRIBUTE_LABELS.sv.quantity
  )
    ? "sv"
    : "en";
}

async function buildDesignSummary(
  configuration: StickerConfiguration,
  resolveBackgroundById: CustomerFacingBackgroundResolver,
  resolveIconById: CustomerFacingIconResolver,
  resolveTextColorById: CustomerFacingTextColorResolver
): Promise<string> {
  const [backgroundOption, iconOption, textColorOption] = await Promise.all([
    resolveBackgroundById(configuration.background),
    configuration.icon === null ? Promise.resolve(null) : resolveIconById(configuration.icon),
    resolveTextColorById(configuration.textColor),
  ]);

  const font = getFontById(configuration.font)?.label ?? configuration.font;
  const background = backgroundOption?.label ?? configuration.background;
  const textColor = textColorOption?.label ?? configuration.textColor;
  const icon = configuration.icon === null ? null : (iconOption?.label ?? configuration.icon);

  return [font, background, textColor, icon].filter(Boolean).join(", ");
}

export async function buildCustomerFacingStickerAttributes(options: {
  configuration: StickerConfiguration;
  sheetOrder: StickerSheetOrder;
  locale: AppLocale;
  sizeLabel: string;
  resolveBackgroundById: CustomerFacingBackgroundResolver;
  resolveIconById: CustomerFacingIconResolver;
  resolveTextColorById: CustomerFacingTextColorResolver;
}): Promise<ShopifyCustomerFacingAttribute[]> {
  const labels = CUSTOMER_FACING_ATTRIBUTE_LABELS[options.locale];

  const displayName = [
    options.configuration.lines.line1,
    options.configuration.lines.line2,
    options.configuration.lines.line3,
  ]
    .map((l) => l.trim())
    .filter((l) => l.length > 0)
    .join(" / ");

  const designSummary = await buildDesignSummary(
    options.configuration,
    options.resolveBackgroundById,
    options.resolveIconById,
    options.resolveTextColorById
  );

  const attributes: ShopifyCustomerFacingAttribute[] = [
    {
      key: labels.name,
      value: displayName,
    },
    {
      key: labels.size,
      value: options.sizeLabel,
    },
    {
      key: labels.quantity,
      value: formatQuantitySummary(options.sheetOrder, options.locale),
    },
    {
      key: labels.design,
      value: designSummary,
    },
  ];

  // No Mix recipe breakdown here (spec #107, #108: "not shown anywhere").
  // These attributes are customer-facing — checkout, order confirmation, the
  // customer's order page. Print never read it: the recipe it packs from is
  // the locked artifact's, in the hidden fulfillment payload.
  return attributes;
}

function cloneLockedProductArtifact(
  artifact: LockedStickerProductArtifact
): LockedStickerProductArtifact {
  if (artifact.kind === "single") {
    return { kind: "single", ref: { ...artifact.ref } };
  }
  const clonedBySize: Partial<Record<SizeId, LockedStickerArtifactReference>> = {};
  for (const [sizeId, ref] of Object.entries(artifact.bySize)) {
    if (ref) {
      clonedBySize[sizeId as SizeId] = { ...ref };
    }
  }
  return {
    kind: "mix",
    bySize: clonedBySize,
    recipe: {
      items: artifact.recipe.items.map((item) => ({ ...item })),
    },
  };
}

export function buildHiddenStickerLineSnapshot(options: {
  cartLineId: string;
  variantId: string;
  configuration: StickerConfiguration;
  lockedArtifact: LockedStickerProductArtifact;
  sheetOrder: StickerSheetOrder;
  customerFacingAttributes: ShopifyCustomerFacingAttribute[];
}): ShopifyHiddenStickerLineSnapshot {
  return {
    cartLineId: options.cartLineId,
    productKey: SHOPIFY_STICKER_PRODUCT_KEY,
    variantId: options.variantId,
    configuration: { ...options.configuration },
    lockedArtifact: cloneLockedProductArtifact(options.lockedArtifact),
    sheetOrder: { ...options.sheetOrder },
    customerFacingAttributes: options.customerFacingAttributes.map((attribute) => ({
      ...attribute,
    })),
  };
}

export function buildHiddenStickerFulfillmentPayload(
  lines: ShopifyHiddenStickerLineSnapshot[]
): ShopifyHiddenStickerFulfillmentPayload {
  return {
    version: SHOPIFY_STICKER_FULFILLMENT_METAFIELD_VERSION,
    lines: lines.map((line) => ({
      ...line,
      configuration: { ...line.configuration },
      lockedArtifact: cloneLockedProductArtifact(line.lockedArtifact),
      sheetOrder: { ...line.sheetOrder },
      customerFacingAttributes: line.customerFacingAttributes.map((attribute) => ({
        ...attribute,
      })),
    })),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isCustomerFacingAttribute(value: unknown): value is ShopifyCustomerFacingAttribute {
  return isRecord(value) && typeof value.key === "string" && typeof value.value === "string";
}

function isLockedArtifactReference(value: unknown): value is LockedStickerArtifactReference {
  return (
    isRecord(value) &&
    value.kind === "canonical-svg" &&
    typeof value.artifactId === "string" &&
    typeof value.rendererVersion === "string" &&
    typeof value.size === "string" &&
    typeof value.trimWidthPx === "number" &&
    typeof value.trimHeightPx === "number" &&
    typeof value.fullArtWidthPx === "number" &&
    typeof value.fullArtHeightPx === "number"
  );
}

function isMixSheetRecipe(value: unknown): value is MixSheetRecipe {
  if (!isRecord(value) || !Array.isArray((value as { items?: unknown }).items)) {
    return false;
  }
  const items = (value as { items: unknown[] }).items;
  return items.every((item): item is MixSheetRecipeItem => {
    if (!isRecord(item)) return false;
    const sizeId = (item as { sizeId?: unknown }).sizeId;
    const count = (item as { count?: unknown }).count;
    return (
      typeof sizeId === "string" &&
      typeof count === "number" &&
      Number.isInteger(count) &&
      count >= 1
    );
  });
}

function isLockedStickerProductArtifact(value: unknown): value is LockedStickerProductArtifact {
  if (!isRecord(value)) return false;
  if (value.kind === "single") {
    return isLockedArtifactReference(value.ref);
  }
  if (value.kind === "mix") {
    if (!isRecord(value.bySize)) return false;
    for (const ref of Object.values(value.bySize)) {
      if (!isLockedArtifactReference(ref)) return false;
    }
    return isMixSheetRecipe(value.recipe);
  }
  return false;
}

function isStickerConfiguration(value: unknown): value is StickerConfiguration {
  if (
    !isRecord(value) ||
    typeof value.font !== "string" ||
    typeof value.background !== "string" ||
    typeof value.textColor !== "string" ||
    (value.icon !== null && typeof value.icon !== "string") ||
    typeof value.quantity !== "number"
  ) {
    return false;
  }

  if (!isRecord(value.lines)) {
    return false;
  }

  const lines = value.lines as Record<string, unknown>;
  if (
    typeof lines.line1 !== "string" ||
    typeof lines.line2 !== "string" ||
    typeof lines.line3 !== "string"
  ) {
    return false;
  }

  if (value.kind === "mix") {
    return typeof value.mixVariantId === "string" && value.mixVariantId.length > 0;
  }

  return typeof value.size === "string";
}

function isStickerSheetOrder(value: unknown): value is StickerSheetOrder {
  return (
    isRecord(value) &&
    value.quantityUnit === "sheet" &&
    typeof value.sheetCount === "number" &&
    typeof value.stickersPerSheet === "number"
  );
}

function isHiddenStickerLineSnapshot(value: unknown): value is ShopifyHiddenStickerLineSnapshot {
  return (
    isRecord(value) &&
    typeof value.cartLineId === "string" &&
    value.productKey === SHOPIFY_STICKER_PRODUCT_KEY &&
    typeof value.variantId === "string" &&
    isStickerConfiguration(value.configuration) &&
    isLockedStickerProductArtifact(value.lockedArtifact) &&
    isStickerSheetOrder(value.sheetOrder) &&
    Array.isArray(value.customerFacingAttributes) &&
    value.customerFacingAttributes.every(isCustomerFacingAttribute)
  );
}

/**
 * Details handed to `onInvalid` when a stored payload fails to parse. We
 * wrote this metafield ourselves, so either reason means a bug (or a version
 * bump missing a migration) — not customer input.
 */
export interface HiddenStickerPayloadInvalidInfo {
  reason: "invalid_json" | "invalid_shape";
  version: string | null;
}

export function parseHiddenStickerFulfillmentPayload(
  rawValue: string | null | undefined,
  // Reporting is a callback rather than a `logServer` import on purpose:
  // this module reaches the CLIENT bundle via cart-lines → CartPageContent,
  // and `log-server.ts` drags in OTel + next/server's `after`, which
  // Turbopack (correctly) refuses in client graphs. Server-only callers
  // pass a logging callback; client-reachable ones stay silent.
  options?: { onInvalid?: (info: HiddenStickerPayloadInvalidInfo) => void }
): ShopifyHiddenStickerFulfillmentPayload | null {
  if (!rawValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawValue);

    if (
      !isRecord(parsed) ||
      parsed.version !== SHOPIFY_STICKER_FULFILLMENT_METAFIELD_VERSION ||
      !Array.isArray(parsed.lines) ||
      !parsed.lines.every(isHiddenStickerLineSnapshot)
    ) {
      // Callers degrade to line-item-property reconstruction, which would
      // otherwise hide the corruption entirely — hence onInvalid.
      options?.onInvalid?.({
        reason: "invalid_shape",
        version: isRecord(parsed) && typeof parsed.version === "string" ? parsed.version : null,
      });
      return null;
    }

    return buildHiddenStickerFulfillmentPayload(parsed.lines);
  } catch {
    options?.onInvalid?.({ reason: "invalid_json", version: null });
    return null;
  }
}

export function getHiddenStickerLineSnapshotByCartLineId(
  payload: ShopifyHiddenStickerFulfillmentPayload | null,
  cartLineId: string
): ShopifyHiddenStickerLineSnapshot | null {
  return payload?.lines.find((line) => line.cartLineId === cartLineId) ?? null;
}

export function filterHiddenStickerFulfillmentPayloadToCartLines(
  payload: ShopifyHiddenStickerFulfillmentPayload | null,
  cartLineIds: readonly string[]
): ShopifyHiddenStickerFulfillmentPayload {
  const allowedIds = new Set(cartLineIds);

  return buildHiddenStickerFulfillmentPayload(
    (payload?.lines ?? []).filter((line) => allowedIds.has(line.cartLineId))
  );
}

export function getCustomerFacingAttributeMap(
  attributes: readonly ShopifyCustomerFacingAttribute[]
): Map<string, string> {
  return new Map(attributes.map((attribute) => [attribute.key, attribute.value]));
}

export function updateCustomerFacingStickerQuantityAttribute(
  attributes: readonly ShopifyCustomerFacingAttribute[],
  sheetOrder: StickerSheetOrder
): ShopifyCustomerFacingAttribute[] {
  const locale = inferCustomerFacingAttributeLocale(attributes);
  const quantityKey = CUSTOMER_FACING_ATTRIBUTE_LABELS[locale].quantity;

  return attributes.map((attribute) =>
    attribute.key === quantityKey
      ? {
          ...attribute,
          value: formatQuantitySummary(sheetOrder, locale),
        }
      : { ...attribute }
  );
}

export function lineEntriesMatchCustomerFacingAttributes(
  entries: readonly ShopifyLineEntry[],
  attributes: readonly ShopifyCustomerFacingAttribute[]
): boolean {
  if (entries.length !== attributes.length) {
    return false;
  }

  const normalizedEntries = entries.map((entry) => ({
    key: "key" in entry ? entry.key : entry.name,
    value: entry.value,
  }));

  return attributes.every(
    (attribute, index) =>
      normalizedEntries[index]?.key === attribute.key &&
      normalizedEntries[index]?.value === attribute.value
  );
}

export function findMatchingHiddenStickerSnapshotForOrderLine(
  payload: ShopifyHiddenStickerFulfillmentPayload | null,
  line: {
    properties: ShopifyLineEntry[];
  }
): ShopifyHiddenStickerLineSnapshot | null {
  if (!payload) {
    return null;
  }

  const candidates = payload.lines.filter((snapshot) =>
    lineEntriesMatchCustomerFacingAttributes(line.properties, snapshot.customerFacingAttributes)
  );

  if (candidates.length !== 1) {
    return null;
  }

  return candidates[0];
}
